import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { enpointCervinoWeb } from 'src/app/configuration/enpointsCervino';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { ProviderInvoiceModel } from 'src/app/models/shopping/provider-invoice.model';
import { ArticleOthersTableModel } from 'src/app/models/tables/shopping/article-others-table.model';
import { ArticleWithEntryTableModel } from 'src/app/models/tables/shopping/article-with-entry-table.model';
import { ArticleWithOCTableModel } from 'src/app/models/tables/shopping/article-with-oc-table.model';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ProviderInvoiceService {

  private readonly _httpClient = inject(HttpClient);

  changeStateAsync(providerInvoiceId: number): Observable<ProviderInvoiceModel> {
    return this._httpClient.put<ProviderInvoiceModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.providerInvoice.ChangeStateAsync}/${providerInvoiceId}`,
      null);
  }

  createProviderInvoice(providerInvoice: ProviderInvoiceModel): Observable<ProviderInvoiceModel> {
    return this._httpClient.post<ProviderInvoiceModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.providerInvoice.CreateProviderInvoice}`,
      providerInvoice);
  }

  deleteProviderInvoice(providerInvoiceId: number): Observable<ProviderInvoiceModel> {
    const params = new HttpParams().set('providerInvoiceId', providerInvoiceId);
    return this._httpClient.delete<ProviderInvoiceModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.providerInvoice.DeleteProviderInvoice}`,
      { params: params }
    );
  }

  exportGeneralConsultation(filters: ColumnResponseModel[], columnSort: string | null, ascendant: boolean): Observable<Blob> {
    const params = new HttpParams().set('columnSort', columnSort ?? '').set('ascendant', ascendant.toString());

    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.providerInvoice.ExportGeneralConsultation}`, filters, {
      params,
      responseType: 'blob',
    });
  }

  getArticleOthers(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<ArticleOthersTableModel>> {
    const params = new HttpParams()
      .set('page', page)
      .set('limit', limit)
      .set('columnSort', columnSort || '')
      .set('ascendant', ascendant);

    return this._httpClient.post<PagedResponseModel<ArticleOthersTableModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.providerInvoice.GetArticleOthersAsync}`,
      filters, { params: params }
    );
  }

  getArticleWithEntry(providerId: number, branchId: number, page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<ArticleWithEntryTableModel>> {
    const params = new HttpParams()
      .set('providerId', providerId)
      .set('branchId', branchId)
      .set('page', page)
      .set('limit', limit)
      .set('columnSort', columnSort || '')
      .set('ascendant', ascendant);

    return this._httpClient.post<PagedResponseModel<ArticleWithEntryTableModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.providerInvoice.GetArticleWithEntryAsync}`,
      filters, { params: params }
    );
  }

  getArticleWithOC(providerId: number, branchId: number, page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<ArticleWithOCTableModel>> {
    const params = new HttpParams()
      .set('providerId', providerId)
      .set('branchId', branchId)
      .set('page', page)
      .set('limit', limit)
      .set('columnSort', columnSort || '')
      .set('ascendant', ascendant);

    return this._httpClient.post<PagedResponseModel<ArticleWithOCTableModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.providerInvoice.GetArticleWithOCAsync}`,
      filters, { params: params }
    );
  }

  getColumnsForArticleOthers(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.providerInvoice.GetColumnsForArticleOthersAsync}`);
  }

  getColumnsForArticleWithEntry(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.providerInvoice.GetColumnsForArticleWithEntryAsync}`);
  }

  getColumnsForArticleWithOC(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.providerInvoice.GetColumnsForArticleWithOCAsync}`);
  }

  getColumnsForGeneralConsultation(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.providerInvoice.GetColumnsForGeneralConsultation}`);
  }

  getDaysExpirationByProviderId(providerId: number): Observable<number> {
    const params = new HttpParams().set('providerId', providerId);
    return this._httpClient.get<number>(`${environment.apiCervinoBackend}${enpointCervinoWeb.providerInvoice.GetDaysExpirationByProviderIdAsync}`,
      { params: params }
    );
  }

  getFirstProviderInvoiceByKeysAsync(branchId: number): Observable<ProviderInvoiceModel> {
    const params = new HttpParams().set('branchId', branchId);
    return this._httpClient.get<ProviderInvoiceModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.providerInvoice.GetFirstProviderInvoiceByKeysAsync}`, { params: params });
  }

  getLastProviderInvoiceByKeysAsync(branchId: number): Observable<ProviderInvoiceModel> {
    const params = new HttpParams().set('branchId', branchId);
    return this._httpClient.get<ProviderInvoiceModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.providerInvoice.GetLastProviderInvoiceByKeysAsync}`, { params: params });
  }

  getNextProviderInvoiceByKeysAsync(branchId: number, consecutive: number): Observable<ProviderInvoiceModel> {
    const params = new HttpParams().set('branchId', branchId).set('consecutive', consecutive);
    return this._httpClient.get<ProviderInvoiceModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.providerInvoice.GetNextProviderInvoiceByKeysAsync}`, { params: params });
  }

  getPreviusProviderInvoiceByKeysAsync(branchId: number, consecutive: number): Observable<ProviderInvoiceModel> {
    const params = new HttpParams().set('branchId', branchId).set('consecutive', consecutive);
    return this._httpClient.get<ProviderInvoiceModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.providerInvoice.GetPreviusProviderInvoiceByKeysAsync}`, { params: params });
  }

  getProviderInvoiceById(providerInvoiceId: number): Observable<ProviderInvoiceModel> {
    const params = new HttpParams().set('providerInvoiceId', providerInvoiceId);
    return this._httpClient.get<ProviderInvoiceModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.providerInvoice.GetProviderInvoiceById}`,
      { params: params }
    );
  }

  getProviderInvoiceByKeys(branchId: number, providerId: number, prefix: string | null, invoiceNumber: number): Observable<ProviderInvoiceModel> {
    const params = new HttpParams()
      .set('branchId', branchId)
      .set('providerId', providerId)
      .set('prefix', prefix || '')
      .set('invoiceNumber', invoiceNumber);
    return this._httpClient.get<ProviderInvoiceModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.providerInvoice.GetProviderInvoiceByKeysAsync}`,
      { params: params }
    );
  }

  getProviderInvoicesForGeneralConsultation(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<any>> {
    const params = new HttpParams()
      .set('page', page)
      .set('limit', limit)
      .set('columnSort', columnSort || '')
      .set('ascendant', ascendant);

    return this._httpClient.post<PagedResponseModel<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.providerInvoice.GetProviderInvoicesForGeneralConsultation}`,
      filters, { params: params }
    );
  }

  updateProviderInvoice(providerInvoice: ProviderInvoiceModel): Observable<ProviderInvoiceModel> {
    return this._httpClient.put<ProviderInvoiceModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.providerInvoice.UpdateProviderInvoice}`,
      providerInvoice);
  }
}
